<script setup lang="ts">
import { PopoverArrow, PopoverClose, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";

import { useWindow } from "@/hooks/use-window";

import type { AriaAttributes, HTMLAttributes } from "vue";
import type { PopoverContentProps, PopoverTriggerProps } from "reka-ui";

withDefaults(defineProps<{
  trigger?: "manual" | "default";
  disabled?: boolean;
  advanced?: boolean;
  confirmText?: string;
  triggerProps?: PopoverTriggerProps & AriaAttributes & HTMLAttributes;
  contentProps?: PopoverContentProps;
  isLoading?: boolean;
}>(), { confirmText: "Yes",trigger: "default", })
defineSlots<{
  question(): unknown;
  trigger(props: { open: typeof showPopover }): unknown;
}>();
defineEmits<{
  (e: "confirmed"): void;
  (e: "canceled"): void;
}>();
const openPopover = defineModel<boolean>("open", { default: false });

const window = useWindow();

function showPopover() {
  openPopover.value = true;
}

function togglePopoverOpen(state: boolean) {
  if (__props.trigger === "manual" && state) return;
  openPopover.value = state;
}
</script>
<template>
  <PopoverRoot :open="openPopover" @update:open="togglePopoverOpen">
    <PopoverTrigger :disabled="disabled" :as-child="triggerProps?.asChild ?? false" :="triggerProps" @click.stop>
      <slot name="trigger" :open="showPopover" />
    </PopoverTrigger>
    <PopoverPortal :to="window.document.body">
      <PopoverContent
        align="start"
        avoid-collisions
        class="tw-b-r tw-z-[1000000] tw-flex tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-flex-col tw-gap-1 tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-2 tw-shadow"
        :align-offset="5"
        :="contentProps"
        :arrow-padding="10"
      >
        <PopoverArrow class="tw-fill-black/50" />
        <template v-if="advanced">
          <slot name="question" />
        </template>
        <template v-else>
          <p class="tw-max-w-xs tw-text-balance">
            <slot name="question" />
          </p>
          <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
            <PopoverClose class="tw-btn-md-white tw-b-r" @click="() => $emit('canceled')">Cancel</PopoverClose>
            <button
              type="button"
              class="tw-btn-md-gray tw-b-r tw-flex tw-items-center tw-gap-1"
              :disabled="disabled"
              @click="() => $emit('confirmed')"
            >
              <i v-if="isLoading" aria-hidden="true" class="fas fa-spin fa-circle-notch" />
              {{ confirmText }}
            </button>
          </div>
        </template>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
